import {
  useState,
  useCallback,
  useMemo,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Fade, makeStyles } from '@material-ui/core';
import { presetPeriod } from 'utility/presets';
import CompareFilters from './compareFilters';
import CompareScores from './compareScores';
import AssignUsersDialog from '../assignUsersDialog';
import {
  getObjectToNumberArray,
  isArrayEmpty,
  isValueInList,
  removeObjectFromArray,
  areArraysContentsEqual,
} from '../../../utility/helpers';
import { getSelectedSurveyType } from '../../../utility/presets';
import { getStartEndOfDayUtc } from '../../../utility/dateUtils';
import { getLast12Months } from '../customDateRange/config';
import {
  SURVEY_TYPES,
  INITIAL_SURVEY_TYPE_FILTER_VALUE,
} from '../../../constants/survey';
import { PARAMS } from '../../../constants/pages';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  contentRoot: {
    boxSizing: 'border-box',
    border: `1px solid ${primary.bluish7}`,
    borderRadius: 4,
    display: 'grid',
    gridTemplateColumns: 'minmax(0,1fr)',
    padding: spacing(4),
    width: '50%',
  },
  contentRootFullWidth: {
    gridTemplateColumns: 'repeat(2, minmax(0,1fr))',
    gridColumnGap: 32,
    width: '100%',
  },
  createdForWrapper: {
    position: 'relative',
    padding: spacing(4),
  },
  createdForWrapperDivider: {
    '&::before': {
      content: '""',
      position: 'absolute',
      width: 1,
      height: '100%',
      top: 0,
      right: -1,
      borderRight: `1px dotted ${primary.bluish7}`,
    },
  },
  compareUserWrapper: {
    padding: spacing(4),
  },
  avatarWrapper: {
    display: 'flex',
    alignItems: 'center',
    width: 'auto',
    maxWidth: 'calc(100% - 34px)',
  },
  userWrapper: {
    justifyContent: 'flex-start',
    width: '100%',
  },
  userLabel: {
    fontFamily: 'ProximaNova-Bold',
    fontSize: 16,
    lineHeight: '20px',
    color: primary.blue1,
  },
  userCaption: {
    maxWidth: 'calc(100% - 56px)',
  },
  field: {
    marginTop: spacing(6),
    width: '100%',
    maxWidth: 300,
  },
  peoplePicker: {
    width: '100%',
    maxWidth: '100%',
  },
  selectedReviewerWrapper: {
    maxWidth: 380,
  },
  topSpacing: {
    marginTop: spacing(4),
  },
  syncFiltersButton: {
    position: 'absolute',
    right: 16,
    top: 25,
  },
  noAttributes: {
    marginTop: spacing(6),
  },
}));

const UserCompareComponent = function (
  {
    translations,
    createdForUser,
    allUsers,
    allAccessibleUsers,
    initialPeriod = null,
    initialReviewers = [],
    initialSurveyType = null,
    initialPreset = {},
    userFilters,
    surveyTypeOptions,
    onGetAttributeScores,
    onGetReviewers,
    canAccessUserProfileCheck,
    onGoToProfilePage,
    checkValueInPresets,
    onCompareWithDisabledPresetMessage,
    onCreatedForDisabledPresetMessage,
    onFilterDataChange,
  },
  ref
) {
  const classes = useStyles();

  const createdFor =
    initialPreset && initialPreset?.data?.createdFor
      ? initialPreset.data.createdFor
      : null;
  const compareWith =
    initialPreset && initialPreset?.data?.compareWith
      ? initialPreset.data.compareWith
      : null;

  const getFullUserObject = useCallback(
    users =>
      users ? allUsers.filter(user => isValueInList(users, user.id)) : null,
    [allUsers]
  );
  const [createdForPreset, setCreatedForPreset] = useState(createdFor);
  const [compareWithPreset, setCompareWithPreset] = useState(compareWith);
  const [isCreatedForAttributesLoading, setIsCreatedForAttributesLoading] =
    useState(!isArrayEmpty(initialReviewers));
  const [createdForAttributes, setCreatedForAttributes] = useState([]);
  const [compareWithAttributes, setCompareWithAttributes] = useState([]);
  const [createdForSurveyType, setCreatedForSurveyType] = useState(
    getSelectedSurveyType(surveyTypeOptions, createdFor?.survey_type) ||
      initialSurveyType ||
      INITIAL_SURVEY_TYPE_FILTER_VALUE
  );
  const [createdForPeriod, setCreatedForPeriod] = useState(
    presetPeriod(createdForPreset) || initialPeriod || getLast12Months()
  );
  const [createdForReviewers, setCreatedForReviewers] = useState(
    getFullUserObject(createdForPreset?.reviewer) || initialReviewers
  );
  const [compareWithSurveyType, setCompareWithSurveyType] = useState(
    getSelectedSurveyType(surveyTypeOptions, compareWith?.survey_type) ||
      INITIAL_SURVEY_TYPE_FILTER_VALUE
  );
  const [compareWithPeriod, setCompareWithPeriod] = useState(
    presetPeriod(compareWithPreset) || getLast12Months()
  );
  const [compareWithReviewers, setCompareWithReviewers] = useState(
    getFullUserObject(compareWith?.reviewer) || []
  );
  const [compareWithUser, setCompareWithUser] = useState(
    compareWithPreset?.subject
  );
  const [isAddCompareOpened, setIsAddCompareOpened] = useState(false);
  const [hasCreatedForRestore, setCreatedForRestore] = useState(false);
  const [hasCompareWithRestore, setCompareWithRestore] = useState(false);

  const isCreatedForSelf = areArraysContentsEqual(createdForSurveyType.value, [
    SURVEY_TYPES.SELF,
  ]);
  const isCompareWithSelf = areArraysContentsEqual(
    compareWithSurveyType.value,
    [SURVEY_TYPES.SELF]
  );
  const createdForParams = useMemo(() => {
    const { SURVEY_TYPE, START, END, START_AT, END_AT, SUBJECT } = PARAMS;
    const reviewer = getObjectToNumberArray(createdForReviewers);

    return {
      [SURVEY_TYPE]: createdForSurveyType.value,
      [START_AT]: createdForPreset
        ? createdForPreset.from_date
        : getStartEndOfDayUtc(createdForPeriod[START]),
      [END_AT]: createdForPreset
        ? createdForPreset.to_date
        : getStartEndOfDayUtc(createdForPeriod[END], true),
      [SUBJECT]: createdForUser?.id,
      ...(isCreatedForSelf ? {} : { reviewer }),
    };
  }, [
    createdForPreset,
    createdForUser,
    createdForReviewers,
    createdForSurveyType,
    createdForPeriod,
    isCreatedForSelf,
  ]);

  const compareWithParams = useMemo(() => {
    const { SURVEY_TYPE, START, END, START_AT, END_AT, SUBJECT } = PARAMS;
    const reviewer = getObjectToNumberArray(compareWithReviewers);

    return {
      [SURVEY_TYPE]: compareWithSurveyType.value,
      [START_AT]: compareWithPreset
        ? compareWithPreset.from_date
        : getStartEndOfDayUtc(compareWithPeriod[START]),
      [END_AT]: compareWithPreset
        ? compareWithPreset.to_date
        : getStartEndOfDayUtc(compareWithPeriod[END], true),
      [SUBJECT]: compareWithUser?.id,
      ...(isCompareWithSelf ? {} : { reviewer }),
    };
  }, [
    compareWithPreset,
    compareWithUser,
    compareWithReviewers,
    compareWithSurveyType,
    compareWithPeriod,
    isCompareWithSelf,
  ]);

  useImperativeHandle(ref, () => ({
    getCreatedForData() {
      return { ...createdForParams, subject: createdForUser };
    },
    getCompareWithData() {
      return { ...compareWithParams, subject: compareWithUser };
    },
    updateCreatedForPreset(data) {
      setCreatedForPreset(data);
      if (data) {
        setCreatedForReviewers(getFullUserObject(data?.reviewer) || []);
        setCreatedForPeriod(data ? presetPeriod(data) : getLast12Months());
        setCreatedForSurveyType(
          getSelectedSurveyType(surveyTypeOptions, data?.survey_type)
        );
      }
    },
    updateCompareWithPreset(data) {
      setCompareWithPreset(data);
      if (data) {
        if (data?.subject) {
          setCompareWithReviewers(getFullUserObject(data?.reviewer) || []);
          setCompareWithUser(data?.subject);
          setCompareWithPeriod(data ? presetPeriod(data) : getLast12Months());
          setCompareWithSurveyType(
            getSelectedSurveyType(surveyTypeOptions, data?.survey_type) ||
              INITIAL_SURVEY_TYPE_FILTER_VALUE
          );
        } else {
          setCompareWithUser(null);
          setCompareWithSurveyType(INITIAL_SURVEY_TYPE_FILTER_VALUE);
          setCompareWithPeriod(getLast12Months());
          setCompareWithReviewers([]);
          setCompareWithAttributes([]);
        }
      }
    },
  }));

  const handleCreatedForSurveyTypeChange = selectedType => {
    const isSelfCompare = areArraysContentsEqual(selectedType.value, [
      SURVEY_TYPES.SELF,
    ]);
    const tempParams = {
      ...createdForParams,
      survey_type: selectedType.value,
      reviewer: isSelfCompare
        ? []
        : getObjectToNumberArray(createdForReviewers),
    };
    setCreatedForSurveyType(selectedType);
    onCreatedForDisabledPresetMessage(tempParams);
    checkValueInPresets(tempParams, compareWithParams);
  };

  const handleCreatedForPeriodChange = newRange => {
    const { START, END } = PARAMS;
    setCreatedForPeriod(newRange);
    checkValueInPresets(
      {
        ...createdForParams,
        from_date: getStartEndOfDayUtc(newRange[START]),
        to_date: getStartEndOfDayUtc(newRange[END], true),
      },
      compareWithParams
    );
  };

  const handleCreatedForReviewersChange = useCallback(
    selectedUsers => {
      const tempParams = {
        ...createdForParams,
        reviewer: getObjectToNumberArray(selectedUsers),
      };
      setCreatedForReviewers(selectedUsers);
      onCreatedForDisabledPresetMessage(tempParams);
      checkValueInPresets(tempParams, compareWithParams);
    },
    [
      createdForParams,
      checkValueInPresets,
      compareWithParams,
      onCreatedForDisabledPresetMessage,
    ]
  );

  const handleCompareWithSurveyTypeChange = selectedType => {
    const isSelfCompare = areArraysContentsEqual(selectedType.value, [
      SURVEY_TYPES.SELF,
    ]);
    const tempParams = {
      ...compareWithParams,
      survey_type: selectedType.value,
      reviewer: isSelfCompare
        ? []
        : getObjectToNumberArray(compareWithReviewers),
    };
    setCompareWithSurveyType(selectedType);
    onCompareWithDisabledPresetMessage(tempParams);
    checkValueInPresets(createdForParams, tempParams);
  };

  const handleCompareWithPeriodChange = newRange => {
    const { START, END } = PARAMS;
    setCompareWithPeriod(newRange);
    checkValueInPresets(createdForParams, {
      ...compareWithParams,
      from_date: getStartEndOfDayUtc(newRange[START]),
      to_date: getStartEndOfDayUtc(newRange[END], true),
    });
  };

  const handleCompareWithReviewersChange = useCallback(
    selectedUsers => {
      const tempParams = {
        ...compareWithParams,
        reviewer: getObjectToNumberArray(selectedUsers),
      };
      setCompareWithReviewers(selectedUsers);
      onCompareWithDisabledPresetMessage(tempParams);
      checkValueInPresets(createdForParams, tempParams);
    },
    [
      createdForParams,
      checkValueInPresets,
      compareWithParams,
      onCompareWithDisabledPresetMessage,
    ]
  );

  const toggleAddCompareDialog = () =>
    setIsAddCompareOpened(prevOpened => !prevOpened);

  const handleSelectCompareWith = selectedUser => {
    const tempParams = {
      ...compareWithParams,
      subject: selectedUser,
    };
    setCompareWithUser(selectedUser);
    onCompareWithDisabledPresetMessage(tempParams);
    checkValueInPresets(createdForParams, tempParams);
    toggleAddCompareDialog();
  };

  const handleSyncFilters = () => {
    const { START, END } = PARAMS;
    const tempParams = {
      ...compareWithParams,
      survey_type: createdForSurveyType.value,
      from_date: createdForPeriod[START],
      to_date: createdForPeriod[END],
      reviewer: getObjectToNumberArray(createdForReviewers),
    };

    onCompareWithDisabledPresetMessage(tempParams);
    setCompareWithSurveyType(createdForSurveyType);
    setCompareWithPeriod(createdForPeriod);
    setCompareWithReviewers(createdForReviewers);
    checkValueInPresets(createdForParams, tempParams);
  };

  const handleRemoveCompareWithUser = () => {
    setCompareWithUser(null);
    setCompareWithSurveyType(INITIAL_SURVEY_TYPE_FILTER_VALUE);
    setCompareWithPeriod(getLast12Months());
    setCompareWithReviewers([]);
    setCompareWithAttributes([]);
    onCreatedForDisabledPresetMessage(createdForParams);
    onCompareWithDisabledPresetMessage(null);
    checkValueInPresets(createdForParams, null);
  };

  const handleRemoveAttribute = useCallback(
    (isCreatedFor = false) =>
      attribute => {
        if (isCreatedFor) {
          setCreatedForRestore(true);
          return setCreatedForAttributes(
            removeObjectFromArray(createdForAttributes, attribute)
          );
        }

        setCompareWithRestore(true);
        return setCompareWithAttributes(
          removeObjectFromArray(compareWithAttributes, attribute)
        );
      },
    [compareWithAttributes, createdForAttributes]
  );

  const handleGetCreatedForAttributes = useCallback(async () => {
    const attributes = await onGetAttributeScores(
      createdForParams,
      isCreatedForSelf
    );

    setCreatedForAttributes(attributes);
    setCreatedForRestore(false);
    setIsCreatedForAttributesLoading(false);
  }, [createdForParams, isCreatedForSelf, onGetAttributeScores]);

  const handleGetCompareWithAttributes = useCallback(async () => {
    const attributes = await onGetAttributeScores(
      compareWithParams,
      isCompareWithSelf
    );

    setCompareWithAttributes(attributes);
    setCompareWithRestore(false);
  }, [compareWithParams, isCompareWithSelf, onGetAttributeScores]);

  const handleCreatedForRestore = () => handleGetCreatedForAttributes();

  const handleCompareWithRestore = () => handleGetCompareWithAttributes();

  const handleReorderCreatedForAttributes = attributes =>
    setCreatedForAttributes(attributes);

  const handleReorderCompareWithAttributes = attributes =>
    setCompareWithAttributes(attributes);

  useEffect(() => {
    onCreatedForDisabledPresetMessage(createdForParams);
  }, [createdForParams, onCreatedForDisabledPresetMessage]);

  useEffect(() => {
    onFilterDataChange?.({
      createdFor: { ...createdForParams, subject: createdForUser },
      compareWith: { ...compareWithParams, subject: compareWithUser },
      createdForReviewers: isCreatedForSelf
        ? []
        : getObjectToNumberArray(createdForReviewers),
      compareWithReviewers: isCompareWithSelf
        ? []
        : getObjectToNumberArray(compareWithReviewers),
    });
  }, [
    createdForParams,
    compareWithParams,
    createdForUser,
    compareWithUser,
    createdForReviewers,
    compareWithReviewers,
    isCreatedForSelf,
    isCompareWithSelf,
    onFilterDataChange,
  ]);

  useEffect(() => {
    handleGetCreatedForAttributes();
  }, [handleGetCreatedForAttributes]);

  useEffect(() => {
    handleGetCompareWithAttributes();
  }, [handleGetCompareWithAttributes]);

  return (
    <div>
      <div
        className={classNames(classes.contentRoot, {
          [classes.contentRootFullWidth]: !!compareWithUser,
        })}
      >
        <CompareFilters
          translations={translations}
          user={createdForUser}
          reviewersFilters={userFilters}
          surveyTypeOptions={surveyTypeOptions}
          allUsers={allUsers}
          surveyType={createdForSurveyType}
          period={createdForPeriod}
          reviewers={createdForReviewers}
          compareWithUser={compareWithUser}
          isSelfSurvey={isCreatedForSelf}
          onOpenAddCompare={toggleAddCompareDialog}
          onSurveyTypeChange={handleCreatedForSurveyTypeChange}
          onPeriodChange={handleCreatedForPeriodChange}
          onChangeReviewers={handleCreatedForReviewersChange}
          canAccessUserProfileCheck={canAccessUserProfileCheck}
          onGoToProfilePage={onGoToProfilePage}
          onGetReviewers={onGetReviewers}
          hasCompareWith
        />
        {compareWithUser && (
          <Fade in={!!compareWithUser} unmountOnExit>
            <div>
              <CompareFilters
                translations={translations}
                user={compareWithUser}
                reviewersFilters={userFilters}
                surveyTypeOptions={surveyTypeOptions}
                allUsers={allUsers}
                surveyType={compareWithSurveyType}
                period={compareWithPeriod}
                reviewers={compareWithReviewers}
                attributes={compareWithAttributes}
                isSelfSurvey={isCompareWithSelf}
                onRemoveUser={handleRemoveCompareWithUser}
                onSurveyTypeChange={handleCompareWithSurveyTypeChange}
                onPeriodChange={handleCompareWithPeriodChange}
                onChangeReviewers={handleCompareWithReviewersChange}
                onSyncFilters={handleSyncFilters}
                canAccessUserProfileCheck={canAccessUserProfileCheck}
                onGoToProfilePage={onGoToProfilePage}
                onGetReviewers={onGetReviewers}
              />
            </div>
          </Fade>
        )}
        {!isCreatedForAttributesLoading && (
          <CompareScores
            translations={translations}
            attributes={createdForAttributes}
            hasDivider={!!compareWithUser}
            hasRestore={hasCreatedForRestore}
            onRemoveAttribute={handleRemoveAttribute(true)}
            onRestore={handleCreatedForRestore}
            onReorder={handleReorderCreatedForAttributes}
            isRemovable
            isDraggable
          />
        )}
        {compareWithUser && (
          <Fade in={!!compareWithUser} unmountOnExit>
            <div>
              <CompareScores
                translations={translations}
                attributes={compareWithAttributes}
                hasRestore={hasCompareWithRestore}
                onRemoveAttribute={handleRemoveAttribute()}
                onRestore={handleCompareWithRestore}
                onReorder={handleReorderCompareWithAttributes}
                isRemovable
                isDraggable
              />
            </div>
          </Fade>
        )}
      </div>
      <AssignUsersDialog
        translations={translations.addCompareWithDialog}
        isOpened={isAddCompareOpened}
        initialValue={compareWithUser}
        allUsers={allAccessibleUsers}
        onCancel={toggleAddCompareDialog}
        onSave={handleSelectCompareWith}
        shouldReturnOption
      />
    </div>
  );
};

UserCompareComponent.propTypes = {
  translations: PropTypes.object.isRequired,
  allUsers: PropTypes.arrayOf(PropTypes.object).isRequired,
  allAccessibleUsers: PropTypes.arrayOf(PropTypes.object).isRequired,
  initialReviewers: PropTypes.arrayOf(PropTypes.object),
  initialPeriod: PropTypes.object,
  initialSurveyType: PropTypes.object,
  createdForUser: PropTypes.object.isRequired,
  surveyTypeOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  userFilters: PropTypes.arrayOf(PropTypes.object).isRequired,
  onGetAttributeScores: PropTypes.func.isRequired,
  canAccessUserProfileCheck: PropTypes.func.isRequired,
  onGoToProfilePage: PropTypes.func.isRequired,
  onGetReviewers: PropTypes.func.isRequired,
  onCompareWithDisabledPresetMessage: PropTypes.func.isRequired,
  onCreatedForDisabledPresetMessage: PropTypes.func.isRequired,
  onFilterDataChange: PropTypes.func.isRequired,
  checkValueInPresets: PropTypes.func.isRequired,
  initialPreset: PropTypes.object,
};

const UserCompare = forwardRef(UserCompareComponent);

UserCompare.displayName = 'UserCompare';

export default UserCompare;
